/**
 * Hygienist Data Service
 * Handles hygienist incentive calculations and data processing
 */

/**
 * Process hygienist data from earnings array
 * @param {Array} earningsArray - Array of practice earnings with hygData
 * @param {string} contractType - Contract type name
 * @returns {Array} Processed hygienist data array
 */
export const processHygienistData = (earningsArray, contractType) => {
  // Only process hygienist data for contracts with w/HYN suffix
  if (!contractType?.includes("w/HYN")) {
    return [];
  }
  
  if (!Array.isArray(earningsArray)) {
    return [];
  }
  
  return earningsArray.flatMap(iPractice => {
    if (!iPractice.hygData) return [];
    
    // Add practice information to each hygienist entry
    Object.values(iPractice.hygData).forEach(hygEntry => {
      hygEntry.practice = iPractice.practiceDBName;
      hygEntry.practiceName = iPractice.practiceInfo?.[0]?.practice;
    });
    
    return iPractice.hygData;
  });
};

/**
 * Generate date-keyed object from hygienist data array
 * @param {Array} hygData - Array of hygienist data objects
 * @returns {Object} Object with dates as keys
 */
export const generateDateKeyObjects = (hygData) => {
  if (!Array.isArray(hygData) || hygData.length === 0) {
    return {};
  }
  
  return Object.assign({}, ...hygData);
};

/**
 * Calculate total hygienist collections for a specific date and practice
 * @param {Object} hygData - Date-keyed hygienist data object
 * @param {string} date - Date string
 * @param {string} practice - Practice name
 * @param {Array} hygAbbreviations - Array of hygienist abbreviations
 * @returns {number} Total collections
 */
export const calculateHygienistCollection = (hygData, date, practice, hygAbbreviations) => {
  if (!hygData || !date || !practice || !Array.isArray(hygAbbreviations)) {
    return 0;
  }
  
  let sumOfHynCollections = 0;
  
  hygAbbreviations.forEach((abbr) => {
    const hygKey = `${abbr}_${date}`;
    const hygEntry = hygData[hygKey];
    
    if (hygEntry && hygEntry.practice === practice) {
      sumOfHynCollections += hygEntry.Amount || 0;
    }
  });
  
  return sumOfHynCollections;
};

/**
 * Check if hygienist data exists for a specific date and practice
 * @param {Object} hygData - Date-keyed hygienist data object
 * @param {string} date - Date string
 * @param {string} practice - Practice name
 * @returns {boolean} True if hygienist data exists
 */
export const hasHygienistDataForDate = (hygData, date, practice) => {
  if (!hygData || !date || !practice) {
    return false;
  }
  
  return hygData[date] && hygData[date].practice === practice;
};

/**
 * Get hygienist billable care for a specific date
 * @param {Object} hygData - Date-keyed hygienist data object
 * @param {string} date - Date string
 * @param {number} payBillablePercentage - Pay billable percentage (0-100)
 * @returns {number} Hygienist billable care amount
 */
export const getHygienistBillableCare = (hygData, date, payBillablePercentage) => {
  if (!hygData || !hygData[date] || !hygData[date].billableCare) {
    return 0;
  }
  
  return hygData[date].billableCare * (payBillablePercentage / 100);
};
